import React, { useState, useEffect, useCallback,memo } from "react";
import {Link, useSearchParams} from "react-router-dom";
import axios from "axios";
import Loader from "../Loader";
import JobCard from "../Job/JobCard";
import debounce from 'lodash/debounce';
import Breadcrumb from "../Breadcrumb";

const JobList = () => {
  const [jobs, setJobs] = useState([]);
  const [wishList, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const categoryName = params.get("category") ? params.get("category") : "";
  const keywordName = params.get("keyword") ? params.get("keyword") : "";
  const locationName = params.get("location") ? params.get("location") : "";

  const [searchParams, setSearchParams] = useState({
    keyword: keywordName,
    location: locationName,
    jobType: "",
    jobRole: "",
    skill: "",
    experience: "",
    workMode: "",
    category: categoryName,
    page: 1
  });

  const [totalJobs, setTotalJobs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const jobTypes = ["full time", "part time", "internship", "freelance"];
  const experiences = ["1+", "2+", "5+", "7+"];
  const workModes = [
    {name: "Work From Office", value: "wfo"},
    {name: "Work From Home", value: "wfh"},
    {name: "Hybrid", value: "hybrid"},
  ]
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [uniqueSkills, setUniqueSkills] = useState([]);
  const [cities, setCities] = useState([]);

  // Fetch user ID
  const userid = localStorage.getItem("userid");

  // Debounced search function
  const debouncedFetchJobs = useCallback(
    debounce(async (params) => {
      try {
        setLoading(true);
        setError("");

        const queryString = new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== "" && v !== null)
          )
        ).toString();

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}jobs/search-jobs-w?${queryString}`
        );


        setJobs(response.data.jobs);
        setTotalJobs(response.data.totalJobs);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setError("Error loading jobs. Please try again later.");
        setJobs([]);
        setTotalJobs(0);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFetchJobs(searchParams);
  }, [searchParams, debouncedFetchJobs]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [citiesResponse, rolesResponse, skillsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}jobs/cities`),
          axios.get(`${process.env.REACT_APP_API_URL}jobs/roles`),
          // do not use this route for skills data because backend controller contains some issues for now
          // will be fixed later...
          // axios.get(`${process.env.REACT_APP_API_URL}jobs/skills`)
        ]);
        const first5Cities = citiesResponse.data.slice(0, 5);

        setCities(first5Cities);
        setUniqueRoles(rolesResponse.data);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchWishlist = async () => {
      if (!userid) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}job_wishlist/getwishlistbyuserid/${userid}`
        );
        setWishlist(response.data.data || []);
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    fetchWishlist();
  }, [userid]);

  const updateSearchParam = (key, value) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filter changes
    }));
  };

  const handlePageChange = (pageNumber) => {
    setSearchParams(prev => ({
      ...prev,
      page: pageNumber
    }));
  };

  const resetFilters = () => {
    setSearchParams({
      keyword: "",
      location: "",
      jobType: "",
      jobRole: "",
      skill: "",
      experience: "",
      workMode: "",
      category: "",
      page: 1
    });
  };

  const isInWishlist = (job_id) => {
    return wishList.some((wish) => wish.job_id === job_id);
  };

  return (
    <>
      {/* <div className="site-breadcrumb pb-5">
        <div>
          <h2 className="breadcrumb-title">Job List</h2>
          <ul className="breadcrumb-menu">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="active">Job List</li>
          </ul> */}
          <Breadcrumb
        title="Job List"
        path={[
          { label: "Home", href: "/", active: false },
          { label: "Job List", active: true }
        ]}
      />

          <div className="search-form-container mb-3 ">
            <div className="search-form-card p-4 ">
              <div className="row g-1">
                <div className="col-md-3">
                  <div className="input-group search-input-group">
                    <span className="input-group-text">
                      <i className="fe-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control search-input p-1"
                      placeholder="Type Keyword..."
                      value={searchParams.keyword}
                      onChange={(e) => updateSearchParam('keyword', e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="input-group search-input-group">
                    <span className="input-group-text">
                      <i className="fe-map-pin"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control search-input p-1"
                      placeholder="Location"
                      value={searchParams.location}
                      onChange={(e) => updateSearchParam('location', e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select search-select"
                    value={searchParams.jobType}
                    onChange={(e) => updateSearchParam('jobType', e.target.value)}
                  >
                    <option value="">Job Type</option>
                    {jobTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 d-flex flex-column justify-content-center">
                  <button
                    className="btn btn-primary w-75 h-100 mx-2"
                    onClick={() => debouncedFetchJobs(searchParams)}
                    disabled={loading}
                  >
                    Search
                  </button>
                </div>
              </div>
              {error && <div className="text-danger mt-2">{error}</div>}
            </div>
          </div>
        {/* </div>
      </div> */}

      <div className="job-grid py-50">
        <div className="container">
          <div className="row">
            {/* Sidebar Filters */}
            <div className="col-lg-4 col-xl-3 mb-4">
              <div className="job-sidebar">
                {/* Reset Filters Button */}
                <div>
                  <button
                    className="btn btn-primary mb-3"
                    onClick={resetFilters}
                  >
                    Reset <i className="fa fa-refresh"></i>
                  </button>
                </div>

                <div className="job-sidebar-item">
                  <h4 className="job-sidebar-title">Cities</h4>
                  <div className="job-type">
                    {cities.map((city) => (
                      <div className="form-check" key={city.id}>
                        <input
                          id={city.id}
                          className="form-check-input"
                          type="radio"
                          name="city"
                          value={city.value}
                          checked={searchParams.location === city.value}
                          onChange={() => updateSearchParam('location', city.value)}
                        />
                        <label className="form-check-label text-capitalize" htmlFor={city.id}>
                          {city.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <br/>
                <div className="job-sidebar-item">
                  <h4 className="job-sidebar-title">Work Mode</h4>
                  <div className="job-type">
                    {workModes.map((work) => (
                      <div className="form-check" key={work.value}>
                        <input
                          id={work.value}
                          className="form-check-input"
                          type="radio"
                          name="workMode"
                          value={work.value}
                          checked={searchParams.workMode === work.value}
                          onChange={() => updateSearchParam('workMode', work.value)}
                        />
                        <label className="form-check-label text-capitalize" htmlFor={work.value}>
                          {work.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8 col-xl-9">
              <div className="job-sort">
                <div className="col-md-5 job-sort-box">
                  <select
                    className="form-control"
                    value={searchParams.jobRole}
                    onChange={(e) => updateSearchParam('jobRole', e.target.value)}
                  >
                    <option value="">Filter by Roles</option>
                    {uniqueRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-5 job-sort-box">
                  <select
                    className="form-control"
                    value={searchParams.experience}
                    onChange={(e) => updateSearchParam('experience', e.target.value)}
                  >
                    <option value="">Filter by Experience</option>
                    {experiences.map((exp) => (
                      <option key={exp} value={exp}>
                        {exp} Years
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row m-2">
                <h5>{totalJobs} Results Found</h5>
              </div>

              <div className="row">
                {loading ? (
                  <Loader />
                ) : jobs.length > 0 ? (
                  jobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      isInWishlist={isInWishlist}
                    />
                  ))
                ) : (
                  <div className="site-heading text-center">
                    <h2>
                      No jobs found matching your criteria. Please try different search terms.
                    </h2>
                  </div>
                )}
              </div>

              {totalJobs > 0 && (
                <div className="pagination-area">
                  <ul className="pagination">
                    {/* Previous Page Button */}
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="far fa-angle-double-left" />
                      </button>
                    </li>

                    {[...Array(totalPages)].map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="far fa-angle-double-right" />
                      </button>
                    </li>
                  </ul>

                  <div className="pagination-showing">
                    <p>
                      Showing {(currentPage - 1) * 4 + 1} - {Math.min(currentPage * 4, totalJobs)} of {totalJobs} Jobs
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(JobList);